import React from "react"
import cn from "../../../utils/classes"
import { ButtonWrapper } from "./ButtonWrapper"
import Link from "./Link"
// import copyrightYear from './../../../Assets/copyrightYear.svg'; this will need to be modified when we get designs

export interface FooterProps {
  contactLink: () => void
  onAboutClick?: () => void
  shouldShowAboutLink?: boolean
  legalPrivacyLink: string
  termsLink: string
  text: {
    contactText: string
    aboutText: string
    legalAndPrivacyText: string
    termsAndConditionsText: string
  }
}

/**
 * Footer component for displaying contact, legal/privacy, terms, and optional about links.
 *
 * - Renders a responsive footer with Contact, Legal & Privacy, Terms & Conditions, and optionally About.
 * - Handles keyboard accessibility for Contact and About buttons.
 * - Uses Tailwind CSS utility classes for layout and styling.
 *
 * @component
 * @property contactLink - Callback for the Contact button click.
 * @property onAboutClick - Optional callback for the About button click.
 * @property shouldShowAboutLink - Whether to display the About button/link.
 * @property legalPrivacyLink - URL for the Legal & Privacy link.
 * @property termsLink - URL for the Terms & Conditions link.
 * @property text - Object containing all display text for the footer.
 *   @property contactText - Text for the Contact button.
 *   @property aboutText - Text for the About button.
 *   @property legalAndPrivacyText - Text for the Legal & Privacy link.
 *   @property termsAndConditionsText - Text for the Terms & Conditions link.
 *
 * @returns {React.Component} The rendered Footer component.
 */
export const Footer: React.FC<FooterProps> = ({
  contactLink,
  onAboutClick,
  shouldShowAboutLink,
  legalPrivacyLink,
  termsLink,
  text,
}) => {
  const borderStyle =
    "border border-solid border-shadow-grey border-t-0 border-l-0 border-r-0 md:border-b-0 md:border-r-2"
  const commonStyle = "flex flex-col md:flex-row px-2 py-4 md:py-0"

  const handleOnKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
      contactLink()
    }
  }

  return (
    <footer
      role="contentinfo"
      className="bg-white w-full flex flex-col items-left shadow-dark md:flex-row md:items-center md:mx-0 md:py-4 mt-4"
    >
      <span className={cn(commonStyle, borderStyle)}>
        <ButtonWrapper
          onClick={contactLink}
          onKeyDown={handleOnKeyDown}
          styleOverrides="text-xs text-gray-700"
        >
          {text.contactText}
        </ButtonWrapper>
      </span>
      <span className={cn(commonStyle, borderStyle)}>
        <Link
          destination={legalPrivacyLink}
          text={text.legalAndPrivacyText}
          styleOverrides="text-xs text-gray-700"
        />
      </span>
      <span
        className={
          shouldShowAboutLink
            ? cn(commonStyle, borderStyle)
            : cn(commonStyle, "pr-0")
        }
      >
        <Link
          destination={termsLink}
          text={text.termsAndConditionsText}
          styleOverrides="text-xs text-gray-700"
        />
      </span>
      {shouldShowAboutLink && (
        <span className={cn(commonStyle, "pr-0")}>
          <ButtonWrapper
            onClick={() => onAboutClick?.()}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "Enter") {
                event.preventDefault()
                onAboutClick?.()
              }
            }}
            styleOverrides="text-xs text-gray-700 px-0"
          >
            {text?.aboutText}
          </ButtonWrapper>
        </span>
      )}
    </footer>
  )
}
